import { Stack, Box, Icons, NoData } from '@cast/design-system';

import { WidgetLayout } from '../../../_components/ui/WidgetLayout';
import { FailedToLoad } from '@cast/shared-messages-ui';

import { useInventoryWorkloadsMetrics } from '../_hooks/useInventoryWorkloadsMetrics';
import { useInventoryWorkloadsTimeRangeSelection } from '../../../_hooks/useInventoryWorkloadsTimeRangeSelection';
import { useDisabledMetricsBoxes } from '@cast/features-cluster-shared';
import { WorkloadsSummaryMetricsChartDataKey } from './_components/_types';

import { ResourceChart } from '../../../_components/ui/ResourceChart';

import { ResourceAvgMetrics } from './_components/ResourceAvgMetrics';
import { inventoryWorkloadsChartZoomProps } from '../../../_hooks/useInventoryWorkloadsChartZoomProps';
import { chartDataSettings } from './_components/_constants';

export const MemoryUtilizationChart = () => {
  const { timeZone, getRange } = useInventoryWorkloadsTimeRangeSelection();
  const { toggleMetric, isMetricDisabled } =
    useDisabledMetricsBoxes<WorkloadsSummaryMetricsChartDataKey>();
  const { from, to } = getRange();

  const {
    dataOrFixture: summaryMetrics,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useInventoryWorkloadsMetrics();

  const hasError = !!error;
  const isChartLoading = isLoading || (hasError && isFetching);
  const memoryData = summaryMetrics?.memory;
  const showEmptyState =
    !isChartLoading && !hasError && (memoryData?.length ?? 0) === 0;

  return (
    <WidgetLayout title="Memory utilization" testId="memory-utilization-chart">
      {hasError && !isFetching ? (
        <FailedToLoad
          title="Failed to load memory utilization"
          refresh={refetch}
        />
      ) : showEmptyState ? (
        <NoData
          title="No data found"
          body="Try selecting a different time period."
          icon={<Icons.Question weight="fill" />}
          testId="memory-utilization-chart-empty"
        />
      ) : (
        <Stack gap={12} height="100%">
          <ResourceAvgMetrics
            data={memoryData}
            unit="GiB"
            toggleMetric={toggleMetric}
            isMetricDisabled={isMetricDisabled}
            isLoading={isChartLoading}
            testId="memory-utilization-chart-avg-metrics"
          />

          <Box flex={1} display="flex">
            <ResourceChart
              data={memoryData ?? []}
              isLoading={isChartLoading}
              unit="GiB"
              isMetricDisabled={(key) =>
                isMetricDisabled(key as WorkloadsSummaryMetricsChartDataKey)
              }
              timeZone={timeZone}
              chartDataSettings={chartDataSettings}
              dateRange={{ from, to }}
              timeUnit="hour"
              filler={() => ({})}
              zoomProps={inventoryWorkloadsChartZoomProps}
            />
          </Box>
        </Stack>
      )}
    </WidgetLayout>
  );
};
